// Warteschlange für alles, was zum Server muss. Aufträge liegen in
// IndexedDB und werden der Reihe nach abgearbeitet; bei fehlender
// Verbindung bleibt die Schlange stehen und läuft später weiter.
//
// Alle Server-Aufrufe der Erfassung sind wiederholbar (PATCH/PUT mit
// Client-IDs, DELETE), daher ist ein doppelt gesendeter Auftrag harmlos.
//
// Damit nicht jeder Tastendruck eine Anfrage wird, verschmilzt enqueue()
// einen neuen PATCH/PUT mit dem letzten wartenden Auftrag auf denselben
// Pfad – aber nie mit dem, der gerade unterwegs ist.

import type { PhotoStep } from "@umzugsscan/shared";
import { ApiError, NetworkError, type Api } from "./api.ts";

export type JsonOp = {
  id?: number;
  token: string;
  kind: "json";
  method: "PATCH" | "PUT" | "DELETE";
  path: string;
  body?: unknown;
};

export type PhotoOp = {
  id?: number;
  token: string;
  kind: "photo";
  photoId: string;
  roomId: string;
  step: PhotoStep;
  section?: number | null;
  blurScore: number | null;
  brightness: number | null;
};

export type Op = JsonOp | PhotoOp;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Verschmilzt zwei Aufträge, wenn möglich; sonst null. */
export function mergeOps(prev: Op, next: Op): Op | null {
  if (prev.kind !== "json" || next.kind !== "json") return null;
  if (prev.method !== next.method || prev.path !== next.path) return null;
  if (prev.method !== "PATCH" && prev.method !== "PUT") return null;
  if (!isPlainObject(prev.body) || !isPlainObject(next.body)) return { ...prev, body: next.body };
  const body: Record<string, unknown> = { ...prev.body };
  for (const [k, v] of Object.entries(next.body)) {
    // Eine Ebene tief (services: {...}); "progress" ersetzt immer komplett.
    body[k] = k !== "progress" && isPlainObject(v) && isPlainObject(body[k]) ? { ...(body[k] as object), ...v } : v;
  }
  return { ...prev, body };
}

export type SyncState = "gespeichert" | "speichert" | "offline";
export type SyncStatus = { pending: number; state: SyncState };

export type OpStore = {
  addOp(op: Op): Promise<number>;
  putOp(op: Op): Promise<unknown>;
  deleteOp(id: number): Promise<void>;
  listOps(token: string): Promise<Op[]>;
  getBlob(photoId: string): Promise<Blob | null>;
};

export type SyncOptions = {
  token: string;
  db: OpStore;
  api: Api;
  /** Verzögerung vor dem Senden (sammelt Tastendrücke). */
  delayMs?: number;
  retryMs?: number[];
  onStatus?(s: SyncStatus): void;
  onDone?(op: Op): void;
  /** Server meldet "bereits abgesendet" (z. B. auf einem anderen Gerät). */
  onSubmittedElsewhere?(): void;
};

type Outcome = "ok" | "drop" | "retry" | "stop";

export class SyncQueue {
  private o: Required<Omit<SyncOptions, "onStatus" | "onDone" | "onSubmittedElsewhere">> & SyncOptions;
  private chain: Promise<unknown> = Promise.resolve();
  private inflight: number | null = null;
  private running = false;
  private offline = false;
  private stopped = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retries = 0;
  private idleWaiters: (() => void)[] = [];
  status: SyncStatus = { pending: 0, state: "gespeichert" };

  constructor(opts: SyncOptions) {
    this.o = { delayMs: 600, retryMs: [2000, 5000, 10000, 30000], ...opts };
  }

  /** Alle DB-Zugriffe auf die Schlange laufen nacheinander (kein Verschmelzen in einen laufenden Auftrag). */
  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const p = this.chain.then(fn);
    this.chain = p.catch(() => {});
    return p;
  }

  async enqueue(op: Op): Promise<void> {
    await this.serial(async () => {
      const ops = await this.o.db.listOps(this.o.token);
      const last = ops[ops.length - 1];
      const merged = last && last.id !== this.inflight ? mergeOps(last, op) : null;
      if (merged) await this.o.db.putOp(merged);
      else await this.o.db.addOp({ ...op, token: this.o.token });
    });
    await this.refreshStatus();
    this.schedule(this.o.delayMs);
  }

  /** Wartende Fotos eines gelöschten Fotos nicht mehr hochladen. */
  async dropPhoto(photoId: string): Promise<void> {
    await this.serial(async () => {
      for (const op of await this.o.db.listOps(this.o.token)) {
        if (op.kind === "photo" && op.photoId === photoId && op.id !== this.inflight) await this.o.db.deleteOp(op.id!);
      }
    });
    await this.refreshStatus();
  }

  /** Sofort weitermachen (z. B. Browser meldet "online"). */
  kick() {
    this.retries = 0;
    this.schedule(0);
  }

  stop() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
  }

  /** Wartet, bis die Schlange leer ist. Bei fehlender Verbindung: bis sie wieder da ist. */
  async waitIdle(): Promise<void> {
    // Erst gerade eingereihte Aufträge abwarten, dann den echten Stand aus der DB lesen.
    await this.chain;
    await this.refreshStatus();
    if (this.status.pending === 0 && !this.running) return;
    const done = new Promise<void>((resolve) => this.idleWaiters.push(resolve));
    this.kick();
    return done;
  }

  /** Stand aus der DB lesen (nach dem Öffnen einer Aufnahme). */
  init(): Promise<void> {
    return this.refreshStatus();
  }

  private schedule(ms: number) {
    if (this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, ms);
  }

  async flush(): Promise<void> {
    if (this.running || this.stopped) return;
    this.running = true;
    this.setState();
    try {
      for (;;) {
        const op = await this.serial(async () => {
          const [first] = await this.o.db.listOps(this.o.token);
          this.inflight = first?.id ?? null;
          return first;
        });
        if (!op) break;
        const outcome = await this.perform(op);
        await this.serial(async () => {
          this.inflight = null;
          if (outcome === "ok" || outcome === "drop") await this.o.db.deleteOp(op.id!);
        });
        if (outcome === "ok") {
          this.offline = false;
          this.retries = 0;
          this.o.onDone?.(op);
        }
        await this.refreshStatus();
        if (outcome === "retry") {
          const wait = this.o.retryMs[Math.min(this.retries++, this.o.retryMs.length - 1)]!;
          this.schedule(wait);
          return;
        }
        if (outcome === "stop") {
          this.stop();
          this.o.onSubmittedElsewhere?.();
          return;
        }
      }
    } finally {
      this.running = false;
      await this.refreshStatus();
      if (this.status.pending === 0) for (const w of this.idleWaiters.splice(0)) w();
    }
  }

  private async perform(op: Op): Promise<Outcome> {
    try {
      if (op.kind === "photo") {
        const blob = await this.o.db.getBlob(op.photoId);
        if (!blob) return "drop";
        await this.o.api.putPhoto(this.o.token, op, blob);
      } else {
        await this.o.api.request(op.method, op.path, op.body);
      }
      return "ok";
    } catch (e) {
      if (e instanceof NetworkError) {
        this.offline = true;
        return "retry";
      }
      if (e instanceof ApiError) {
        if (e.code === "bereits_abgesendet") return "stop";
        if (e.status >= 500 || e.status === 429) return "retry";
        // Ungültig/nicht gefunden: Wiederholen hilft nicht, sonst hinge die Schlange für immer.
        console.warn("Auftrag verworfen", op, e.code, e.details);
        return "drop";
      }
      throw e;
    }
  }

  private async refreshStatus() {
    const pending = (await this.serial(() => this.o.db.listOps(this.o.token))).length;
    this.status = { ...this.status, pending };
    this.setState();
  }

  private setState() {
    const state: SyncState =
      this.status.pending === 0 && !this.running ? "gespeichert" : this.offline ? "offline" : "speichert";
    this.status = { ...this.status, state };
    this.o.onStatus?.(this.status);
  }
}
